import { useState, useCallback } from 'react';

type FormErrors<T> = Partial<Record<keyof T, string>>;
type FormTouched<T> = Partial<Record<keyof T, boolean>>;
type ValidationFunction<T> = (values: T) => FormErrors<T>;

interface UseFormReturn<T> {
  values: T;
  errors: FormErrors<T>;
  touched: FormTouched<T>;
  isSubmitting: boolean;
  isValid: boolean;
  isDirty: boolean;
  setValue: (field: keyof T, value: any) => void;
  setFieldError: (field: keyof T, error: string) => void;
  setFieldTouched: (field: keyof T, isTouched?: boolean) => void;
  resetForm: () => void;
  handleSubmit: (onSubmit: (values: T) => Promise<void> | void) => Promise<boolean>;
}

/**
 * Custom hook for form state management with TypeScript support
 * @param initialValues - Initial form values
 * @param validationFn - Optional validation function
 * @returns Form state and handlers
 */
export const useForm = <T extends Record<string, any>>(
  initialValues: T,
  validationFn: ValidationFunction<T> | null = null
): UseFormReturn<T> => {
  const [values, setValues] = useState<T>(initialValues);
  const [errors, setErrors] = useState<FormErrors<T>>({});
  const [touched, setTouched] = useState<FormTouched<T>>({});
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);

  const setValue = useCallback((field: keyof T, value: any): void => {
    setValues(prev => ({
      ...prev,
      [field]: value
    }));

    // Clear error when field value changes
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: undefined
      }));
    }
  }, [errors]);

  const setFieldError = useCallback((field: keyof T, error: string): void => {
    setErrors(prev => ({
      ...prev,
      [field]: error
    }));
  }, []);

  const setFieldTouched = useCallback((field: keyof T, isTouched: boolean = true): void => {
    setTouched(prev => ({
      ...prev,
      [field]: isTouched
    }));
  }, []);

  const resetForm = useCallback((): void => {
    setValues(initialValues);
    setErrors({});
    setTouched({});
    setIsSubmitting(false);
  }, [initialValues]);

  const handleSubmit = useCallback(async (onSubmit: (values: T) => Promise<void> | void): Promise<boolean> => {
    setIsSubmitting(true);
    
    try {
      // Run validation if provided
      if (validationFn) {
        const validationErrors = validationFn(values);
        if (Object.keys(validationErrors).length > 0) {
          setErrors(validationErrors);
          setIsSubmitting(false);
          return false;
        }
      }

      // Clear errors and call submit handler
      setErrors({});
      await onSubmit(values);
      return true;
    } catch (error) {
      console.error('Form submission error:', error);
      return false;
    } finally {
      setIsSubmitting(false);
    }
  }, [values, validationFn]);

  const isValid = Object.keys(errors).length === 0;
  const isDirty = Object.keys(touched).length > 0;

  return {
    values,
    errors,
    touched,
    isSubmitting,
    isValid,
    isDirty,
    setValue,
    setFieldError,
    setFieldTouched,
    resetForm,
    handleSubmit
  };
};
